import { enums } from "@shared/constants";
import { ItemTypesMapIdType } from "@shared/constants/enums/enums-types";
import { useAnalyticsContext } from "@shared/contexts/AnalyticsContext/useAnalyticsContext";
import useAnalytics from "@shared/lib/analytics/hooks/useAnalytics";
import { AnalyticEventOptions } from "@shared/lib/analytics/types/analytics-types";
import { TicketBasketType } from "@shared/modules/basket/types";
import { NestedPublicVenueSerializer } from "@shared/modules/events/types/public";
import { TicketItemGroupType } from "@shared/modules/item-groups/types/ticket-item-group-type";

const useAnalyticsHelpers = () => {
  const { enableAnalytics } = useAnalyticsContext();
  const analytics = useAnalytics();

  /**
   * Helper function used for tracking changes to the items in the cart.
   * @param debouncedAmountSelected New amount of items requested. Determines whether we fire 'add to cart' or 'remove from cart' events.
   * @param basket basket
   * @param itemId Item ID of the item
   * @param venue venue
   * @param spAddToCartCB callback function to trigger the UserInteractionLogger `add_to_cart` event
   * @param opts options passed to internal functions
   */
  const trackItemsInCart = async (
    debouncedAmountSelected: number,
    basket: TicketBasketType,
    itemInfo: {
      itemId: number;
      itemName: string;
      itemVariant: string;
      itemType: ItemTypesMapIdType;
    },
    venue: NestedPublicVenueSerializer,
    spAddToCartCB: () => void | Promise<void>,
    opts?: AnalyticEventOptions
  ): Promise<void> => {
    try {
      if (!enableAnalytics) {
        return;
      }

      if (debouncedAmountSelected > 0 && basket.item_groups instanceof Array) {
        // Trigger `AddToCart` tracking tokens asynchronously
        const group = basket?.item_groups.filter(
          (ig: TicketItemGroupType) =>
            ig.type === itemInfo.itemId ||
            ig.product_attribute === itemInfo.itemId ||
            ig.membership_level === itemInfo.itemId
        );

        if (group.length > 0) {
          const newGroup = group[0];
          const formattedGroup = {
            ...newGroup,
            ...analytics.formatGtagTrackingData(
              itemInfo.itemId,
              itemInfo.itemName,
              itemInfo.itemVariant,
              itemInfo.itemType
            ),
          };
          analytics.addToCart(
            formattedGroup,
            debouncedAmountSelected,
            venue,
            opts
          );
          await analytics.trackToken(
            enums.TRACKING_TOKEN_LOCATIONS.LOCATION_ADD_CART.id,
            venue.id,
            basket.item_groups?.map((item) => item.event),
            {
              fbEvent: "AddToCart",
              value: parseFloat(newGroup.price) * debouncedAmountSelected,
              currency: basket.currency,
              content_ids: newGroup.event || newGroup.membership_group,
              content_type: "product_group",
              name: newGroup.description,
            }
          );
          await spAddToCartCB();
        }
      } else {
        analytics.removeFromCart();
      }
    } catch (err) {
      console.error(err); //eslint-disable-line
    }
  };

  return { trackItemsInCart };
};

export default useAnalyticsHelpers;
